import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as store from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import { useParams } from "react-router-dom";
import { IWorkTimeDetailsPage } from "./workTimeBetaDetailsPageMain";
import {
    DayBookingItemBeta,
    getDurationStrBasedOnUnit,
} from "../../models/dayBooking/dayBookingItemBeta";
import { DayBookingTypeItemBeta } from "../../models/dayBookingType/dayBookingTypeItemBeta";
import * as dayBookingService from "../../services/dayBookingService";
import * as baseService from "../../services/baseService";
import {
    GridColDef,
    GridEditInputCell,
    GridRenderCellParams,
    GridRenderEditCellParams,
} from "@mui/x-data-grid-premium";
import { Base } from "../../framework/base";
import MuiMenu from "../framework/muiMenu";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import HdrAutoIcon from "@mui/icons-material/HdrAuto";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import {
    DayBookingState,
    DayBookingUsageType,
    EnumHelper,
    StrMeasureUnit,
} from "../../models/common/enums";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { workHourToListRow } from "../../models/workTime/workTimeDetailsDayBookingList";
import { IWorkHoursItem } from "../../models/workTime/workHoursItem";
import { useTranslation } from "react-i18next";
import { deleteCategoryFromWorkShiftTimeSlot } from "../../services/workTimeBetaService";
import { saveDayBooking, getDayBookingDuration } from "../../services/dayBookingService";
import { DayBookingSaveData } from "../../models/dayBooking/dayBookingSaveData";
import { SalaryRowTypeKindCode } from "../../models/salary/salaryRowTypeItem";
import { MuiEditableDataGrid } from "../framework/editableDataGrid/muiEditableDataGrid";
import { MuiDataGridDatePicker } from "../framework/editableDataGrid/muiDataGridDatePicker";
import { DataGridAutocompleteCell } from "../framework/editableDataGrid/muiDataGridAutocompleteCell";
import { MuiDataGridDurationCell } from "../framework/editableDataGrid/muiDataGridDurationCell";
import { showApiSuccess } from "../framework/formUtils";
import { AppUtils } from "../../models/common/appUtils";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";

type DayBookingRow = DayBookingItemBeta & {
    isAutomatic?: boolean;
    workShiftTimeSlotId?: string;
};

const isFullDayType = (type: DayBookingTypeItemBeta): boolean => {
    if (!type) return false;
    const measureUnit = type.salaryRowTypeMeasureUnit;
    const measureUnitIsDays = [
        StrMeasureUnit.Day,
        StrMeasureUnit.DayFiAlt,
        StrMeasureUnit.DayEn,
    ].includes(measureUnit as StrMeasureUnit);
    const measureUnitIsHours = [
        StrMeasureUnit.Hour,
        StrMeasureUnit.HourFi,
    ].includes(measureUnit as StrMeasureUnit);
    const measureUnitIsAmount = StrMeasureUnit.Piece.valueOf() === measureUnit;
    const allowEntryInDays = type.allowEntryInDays || measureUnitIsDays;
    const isPartialDayBooking =
        !measureUnitIsDays &&
        !(
            !type.allowEntryInHours &&
            allowEntryInDays &&
            !measureUnitIsHours &&
            !measureUnitIsAmount
        );
    return !isPartialDayBooking;
};

interface IWorkTimeDetailsDayBookingListProps {
    reloadList: () => void;
    dayBookingItems: DayBookingItemBeta[];
    dayBookingTypes: DayBookingTypeItemBeta[];
    shiftDailyAllowances: IWorkHoursItem[];
    shiftCostCenterId?: string;
    shiftVehicleId?: string;
    editingDisabled?: boolean;
    onEditingChange?: (editing: boolean) => void;
}

export function WorkTimeDetailsDayBookingList({
    reloadList,
    dayBookingItems,
    dayBookingTypes,
    shiftDailyAllowances,
    shiftCostCenterId,
    shiftVehicleId,
    editingDisabled,
    onEditingChange,
}: IWorkTimeDetailsDayBookingListProps) {
    const [loading, setLoading] = useState(false);
    const [editId, setEditIdRaw] = useState<string>();
    const setEditId = useCallback((id: string) => {
        setEditIdRaw(id);
        onEditingChange?.(!!id);
    }, [onEditingChange]);
    const { employeeId, date } = useParams<IWorkTimeDetailsPage>();
    const theme = useTheme();
    const { t } = useTranslation();

    // Reset edit state when navigating to a different employee, date, or data changes
    useEffect(() => {
        setEditId(null);
    }, [date, employeeId, dayBookingItems]);

    const allLocked =
        dayBookingItems.length > 0 &&
        dayBookingItems.every((i) =>
            EnumHelper.isEqual(i.state, DayBookingState.Locked)
        );

    const vehicleOptions = store.useAppSelector((state) => state.vehicle.options);
    const costCenterOptions = store.useAppSelector(
        (state) => state.costCenter.groupedOptions
    );

    const rows: DayBookingRow[] = useMemo(
        () => [
            ...(dayBookingItems ?? []).map(
                (item) => ({ ...item, isAutomatic: false } as DayBookingRow)
            ),
            ...(shiftDailyAllowances ?? []).map((a) => {
                const listRow = workHourToListRow(
                    a,
                    costCenterOptions,
                    vehicleOptions,
                    dayBookingTypes
                );
                return {
                    id: listRow.id,
                    rowId: "",
                    employeeId,
                    dayBookingTypeId: listRow.dayBookingTypeId ?? "",
                    costCenterId: listRow.costCenterId ?? "",
                    costCenterName: listRow.costCenterName ?? "",
                    vehicleId: listRow.vehicleId ?? "",
                    vehicleName: listRow.vehicleName ?? "",
                    day: listRow.day,
                    endDay: listRow.endDay,
                    amount: listRow.amount,
                    dayUsageType: listRow.dayUsageType,
                    comment: listRow.comment ?? "",
                    state: listRow.state ?? 0,
                    bookingType: 0,
                    dayHoursRatio: 8,
                    salaryRowTypeId: "",
                    salaryRowTypeMeasureUnit: null,
                    salaryRowTypeDecimals: 0,
                    isAutomatic: true,
                    workShiftTimeSlotId: a.workShiftTimeSlotId,
                } as DayBookingRow;
            }),
        ],
        [
            dayBookingItems,
            shiftDailyAllowances,
            costCenterOptions,
            vehicleOptions,
            dayBookingTypes,
            employeeId,
        ]
    );

    const [gridRows, setGridRows] = useState<DayBookingRow[]>(rows);
    useEffect(() => {
        // Don't overwrite rows while editing — the "new" row would be lost
        if (!editId) {
            setGridRows(rows);
        }
    }, [rows]);

    const dayBookingTypeOptions = useMemo(
        () =>
            (dayBookingTypes ?? []).map((type) => ({
                value: type.id,
                label: type.salaryRowTypeMeasureUnit
                    ? `${type.name} (${type.salaryRowTypeMeasureUnit})`
                    : type.name,
            })),
        [dayBookingTypes]
    );

    const costCenterSelectOptions = useMemo(
        () =>
            (costCenterOptions ?? []).map((o) => ({
                value: o.value,
                label: o.label,
                group: o.group,
            })),
        [costCenterOptions]
    );

    const vehicleSelectOptions = useMemo(
        () =>
            (vehicleOptions ?? []).map((o) => ({ value: o.value, label: o.label })),
        [vehicleOptions]
    );

    const handleEdit = useCallback((id: string) => {
        setEditId(id);
    }, []);

    const handleDelete = useCallback(
        (row: DayBookingRow) => {
            if (!row.id) return;
            store.customStore.dispatch(
                storeActions.setConfirmation(
                    ConfirmationDialogType.Warning,
                    t("common.warning"),
                    t("dayBooking.dayBookingRemoveConfirmation"),
                    () => {
                        store.customStore.dispatch(storeActions.clearConfirmation());
                        setLoading(true);
                        (row.isAutomatic
                            ? deleteCategoryFromWorkShiftTimeSlot(
                                row.workShiftTimeSlotId,
                                row.id.split(".")[1],
                                false
                            )
                            : dayBookingService.deleteDayBooking(row.id)
                        )
                            .then(() => {
                                store.customStore.dispatch(
                                    storeActions.showSuccessMessage(
                                        t("dayBooking.dayBookingRemoveSuccess")
                                    )
                                );
                                reloadList();
                            })
                            .catch((error) => {
                                store.customStore.dispatch(
                                    storeActions.showErrorMessage(
                                        baseService.getErrorMessageFromError(error)
                                    )
                                );
                            })
                            .finally(() => setLoading(false));
                    },
                    () =>
                        store.customStore.dispatch(storeActions.clearConfirmation())
                )
            );
        },
        [reloadList, t]
    );

    const handleLock = useCallback((ids: string[], lock: boolean) => {
        if (ids.length === 0) return;
        setLoading(true);
        (lock
            ? dayBookingService.lockDayBookings(ids)
            : dayBookingService.unLockDayBookings(ids)
        )
            .then((success) => {
                store.customStore.dispatch(
                    storeActions.showSuccessMessage(success.message)
                );
                reloadList();
            })
            .catch((error) => {
                store.customStore.dispatch(
                    storeActions.showErrorMessage(
                        baseService.getErrorMessageFromError(error)
                    )
                );
            })
            .finally(() => setLoading(false));
    }, []);

    const handleAddRow = useCallback(
        (): DayBookingRow =>
            new DayBookingItemBeta({
                employeeId,
                day: date ?? Base.dayjsToJsonDate(),
                endDay: date ?? Base.dayjsToJsonDate(),
            }) as DayBookingRow,
        [employeeId, date]
    );

    const handleSubmit = useCallback(
        async(row: DayBookingRow): Promise<DayBookingRow> => {
            const isNew = !row.id;
            const dayBookingType = dayBookingTypes?.find(
                (type) => type.id === row.dayBookingTypeId
            );
            const fullDay = isFullDayType(dayBookingType);

            let amount: number;
            let dayUsageType: number;
            if (fullDay) {
                const start = new Date(row.day);
                const end = new Date(row.endDay || row.day);
                amount = Base.dateDiffInDays(start, end) + 1;
                dayUsageType = DayBookingUsageType.FullDay;
            } else {
                amount = row.amount ?? 1;
                dayUsageType = DayBookingUsageType.NotFullDay;
            }

            const saveData: DayBookingSaveData = {
                id: isNew ? "" : row.id,
                rowId: isNew ? "" : row.rowId,
                employeeId: row.employeeId || employeeId,
                dayBookingTypeId: row.dayBookingTypeId,
                costCenterId: row.costCenterId || "",
                vehicleId: row.vehicleId || "",
                day: row.day,
                amount,
                dayUsageType,
                comment: row.comment || "",
                automaticCategory: row.automaticCategory || "",
                automaticWorkShiftTimeSlotId:
                    row.automaticWorkShiftTimeSlotId || "",
            };

            const result = await saveDayBooking(saveData);
            showApiSuccess(t("dayBooking.dayBookingSaveSuccessful"));
            reloadList();
            return { ...result, isAutomatic: false } as DayBookingRow;
        },
        [dayBookingTypes, employeeId, reloadList, t]
    );

    const validateRow = useCallback(
        (row: DayBookingRow) => {
            const errors: { field: string; error: string }[] = [];
            if (!row.dayBookingTypeId) {
                errors.push({
                    field: "dayBookingTypeId",
                    error: t("dayBooking.dayBookingTypeRequired"),
                });
            }
            if (!row.day) {
                errors.push({
                    field: "day",
                    error: t("dayBooking.startDateRequired"),
                });
            }
            if (row.day && row.endDay && Base.isBefore(row.endDay, row.day)) {
                errors.push({
                    field: "endDay",
                    error: t("dayBooking.endDayBeforeStartDay"),
                });
            }
            const type = dayBookingTypes?.find((t) => t.id === row.dayBookingTypeId);
            if (type && !isFullDayType(type) && (row.amount === null || row.amount === undefined)) {
                errors.push({
                    field: "amount",
                    error: t("dayBooking.amountRequired"),
                });
            }
            return errors;
        },
        [t, dayBookingTypes]
    );

    // Recalculate amount for full-day types based on date range
    const updateFullDayAmount = useCallback(async(apiRef: React.MutableRefObject<GridApiPremium>, rowId: string | number, dayBookingTypeId?: string) => {
        const row = apiRef.current.getRowWithUpdatedValues(
            rowId,
            "day"
        ) as DayBookingRow;
        const typeId = dayBookingTypeId ?? row?.dayBookingTypeId;
        const type = dayBookingTypes?.find((t) => t.id === typeId);
        if (!type || !isFullDayType(type)) return;

        const day = row.day;
        const endDay = row.endDay || row.day;
        const dayCount = Base.dateDiffInDays(new Date(day), new Date(endDay)) + 1;

        // Vacation types need backend calculation (accounts for holidays/weekends)
        if (type.salaryRowTypeKindCode === SalaryRowTypeKindCode.Vacation.valueOf()) {
            try {
                const duration = await getDayBookingDuration({
                    dayBookingTypeId: typeId,
                    day,
                    amount: dayCount,
                } as DayBookingSaveData);
                void apiRef.current.setEditCellValue({ id: rowId, field: "amount", value: duration });
            } catch {
                // Fallback to simple calculation
                void apiRef.current.setEditCellValue({ id: rowId, field: "amount", value: dayCount });
            }
        } else {
            void apiRef.current.setEditCellValue({ id: rowId, field: "amount", value: dayCount });
        }
    }, [dayBookingTypes]);

    const cols: GridColDef<DayBookingRow>[] = useMemo(
        () => [
            {
                field: "day",
                headerName: t("workTime.starts"),
                width: 110,
                editable: true,
                hideable: false,
                renderCell: (params: GridRenderCellParams<DayBookingRow>) =>
                    params.row.day ? (
                        <span style={{ whiteSpace: "nowrap" }}>
                            {Base.dayjsToDateStr(params.row.day)}
                        </span>
                    ) : null,
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <MuiDataGridDatePicker
                        {...params}
                        onValueChange={(apiRef: React.MutableRefObject<GridApiPremium>, rowId: string, value) => {
                            const row = apiRef.current.getRowWithUpdatedValues(
                                rowId,
                                "day"
                            );
                            const type = dayBookingTypes?.find((t) => t.id === row?.dayBookingTypeId);
                            if (!type || !isFullDayType(type)) {
                                // Non-full-day: sync endDay to day
                                void apiRef.current.setEditCellValue({ id: rowId, field: "endDay", value });
                            }
                            void updateFullDayAmount(apiRef, rowId);
                        }}
                    />
                ),
                sortComparator: (a: string, b: string) =>
                    Base.stringCompare(a, b),
            },
            {
                field: "endDay",
                headerName: t("workTime.ends"),
                width: 110,
                editable: true,
                hideable: false,
                renderCell: (params: GridRenderCellParams<DayBookingRow>) => {
                    const endDay = params.row.endDay || params.row.day;
                    return endDay ? (
                        <span style={{ whiteSpace: "nowrap" }}>
                            {Base.dayjsToDateStr(endDay)}
                        </span>
                    ) : null;
                },
                renderEditCell: (params: GridRenderEditCellParams<DayBookingRow>) => {
                    const type = dayBookingTypes?.find(
                        (t) => t.id === params.row.dayBookingTypeId
                    );
                    if (type && !isFullDayType(type)) {
                        return (
                            <span style={{ padding: "0 10px", whiteSpace: "nowrap" }}>
                                {params.row.endDay ? Base.dayjsToDateStr(params.row.endDay) : ""}
                            </span>
                        );
                    }
                    return (
                        <MuiDataGridDatePicker
                            {...params}
                            onValueChange={(apiRef, rowId) => {
                                void updateFullDayAmount(apiRef, rowId);
                            }}
                        />
                    );
                },
                sortComparator: (a: string, b: string) =>
                    Base.stringCompare(a, b),
            },
            {
                field: "amount",
                headerName: t("common.duration"),
                width: 115,
                editable: true,
                hideable: false,
                type: "number",
                align: "left",
                headerAlign: "left",
                renderCell: (params: GridRenderCellParams<DayBookingRow>) => {
                    const dayBookingType = dayBookingTypes?.find(
                        (type) => type.id === params.row.dayBookingTypeId
                    );
                    if (!dayBookingType)
                        return params.row.amount?.toString() ?? "";
                    const duration = getDurationStrBasedOnUnit(
                        params.row.amount,
                        params.row.dayUsageType,
                        dayBookingType.dayHoursRatio,
                        dayBookingType.salaryRowTypeMeasureUnit
                    );
                    const unit =
                        dayBookingType.salaryRowTypeMeasureUnit ||
                        StrMeasureUnit.Day;
                    return duration ? `${duration} (${unit})` : "";
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    const type = dayBookingTypes?.find(
                        (t) => t.id === params.row.dayBookingTypeId
                    );
                    if (type && isFullDayType(type)) {
                        return (
                            <span style={{ padding: "0 10px" }}>
                                {params.value ?? ""}
                            </span>
                        );
                    }
                    if (type && AppUtils.isTimeUnit(type.salaryRowTypeMeasureUnit)) {
                        return <MuiDataGridDurationCell {...params} />;
                    }
                    return <GridEditInputCell {...params} inputProps={{ min: 0 }} />;
                },
            },
            {
                field: "dayBookingTypeId",
                headerName: t("dayBooking.dayBookingType"),
                flex: 2,
                editable: true,
                hideable: false,
                type: "singleSelect",
                valueOptions: dayBookingTypeOptions,
                renderCell: (params: GridRenderCellParams<DayBookingRow>) => (
                    <>
                        {params.row.isAutomatic ? (
                            <Tooltip
                                title={t("dayBooking.automatic", {
                                    label: (
                                        dayBookingTypes?.find(
                                            (type) =>
                                                type.id ===
                                                params.row.dayBookingTypeId
                                        )?.name ?? ""
                                    ).toLowerCase(),
                                })}
                            >
                                <HdrAutoIcon
                                    color="primary"
                                    sx={{ mr: 0.5 }}
                                />
                            </Tooltip>
                        ) : null}
                        {dayBookingTypes?.find(
                            (type) => type.id === params.row.dayBookingTypeId
                        )?.name ?? ""}
                    </>
                ),
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridAutocompleteCell
                        {...params}
                        onValueChange={(apiRef, rowId, value) => {
                            const type = dayBookingTypes?.find((t) => t.id === value);
                            if (type && !isFullDayType(type)) {
                                // Non-full-day: sync endDay to day
                                const row = apiRef.current.getRowWithUpdatedValues(rowId, "day");
                                void apiRef.current.setEditCellValue({ id: rowId, field: "endDay", value: row.day });
                            }
                            // Vacations and absences can't have cost center or vehicle
                            if (type && (
                                type.salaryRowTypeKindCode === SalaryRowTypeKindCode.Absence.toString() ||
                                type.salaryRowTypeKindCode === SalaryRowTypeKindCode.Vacation.toString()
                            )) {
                                void apiRef.current.setEditCellValue({ id: rowId, field: "costCenterId", value: null });
                                void apiRef.current.setEditCellValue({ id: rowId, field: "vehicleId", value: null });
                            } else if (type) {
                                // Auto-populate from work shift if type flags are set
                                if (type.useWorkShiftCostCenter && shiftCostCenterId) {
                                    void apiRef.current.setEditCellValue({ id: rowId, field: "costCenterId", value: shiftCostCenterId });
                                }
                                if (type.useWorkShiftVehicle && shiftVehicleId) {
                                    void apiRef.current.setEditCellValue({ id: rowId, field: "vehicleId", value: shiftVehicleId });
                                }
                            }
                            void updateFullDayAmount(apiRef, rowId, value);
                        }}
                    />
                ),
            },
            {
                field: "vehicleId",
                headerName: t("vehicle.vehicle"),
                flex: 2,
                editable: true,
                hideable: true,
                type: "singleSelect",
                valueOptions: vehicleSelectOptions,
                renderCell: (params: GridRenderCellParams<DayBookingRow>) =>
                    params.row.vehicleName ?? "",
                renderEditCell: (params: GridRenderEditCellParams) => {
                    const type = dayBookingTypes?.find((t) => t.id === params.row.dayBookingTypeId);
                    if (type && (
                        type.salaryRowTypeKindCode === SalaryRowTypeKindCode.Absence.toString() ||
                        type.salaryRowTypeKindCode === SalaryRowTypeKindCode.Vacation.toString()
                    )) {
                        return <span style={{ padding: "0 10px", color: "#999" }}>-</span>;
                    }
                    return <DataGridAutocompleteCell {...params} />;
                },
            },
            {
                field: "costCenterId",
                headerName: t("workTime.costCenter"),
                flex: 2,
                editable: true,
                hideable: true,
                type: "singleSelect",
                valueOptions: costCenterSelectOptions,
                renderCell: (params: GridRenderCellParams<DayBookingRow>) =>
                    params.row.costCenterName ?? "",
                renderEditCell: (params: GridRenderEditCellParams) => {
                    const type = dayBookingTypes?.find((t) => t.id === params.row.dayBookingTypeId);
                    if (type && (
                        type.salaryRowTypeKindCode === SalaryRowTypeKindCode.Absence.toString() ||
                        type.salaryRowTypeKindCode === SalaryRowTypeKindCode.Vacation.toString()
                    )) {
                        return <span style={{ padding: "0 10px", color: "#999" }}>-</span>;
                    }
                    return <DataGridAutocompleteCell {...params} />;
                },
            },
            {
                field: "comment",
                headerName: t("common.description"),
                flex: 2,
                editable: true,
                hideable: true,
            },
            {
                field: "id",
                headerName: "",
                width: 60,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                hideable: false,
                disableReorder: true,
                pinned: "right",
                renderCell: (params: GridRenderCellParams<DayBookingRow>) => {
                    const row = params.row;
                    const isLocked = EnumHelper.isEqual(
                        row.state,
                        DayBookingState.Locked
                    );
                    const isDisabled = !!editId;
                    const items = isLocked
                        ? [
                            {
                                label: t("common.unlock"),
                                icon: <LockOpenIcon color="primary" />,
                                onClick: () => handleLock([row.id], false),
                            },
                        ]
                        : [
                            ...(!row.isAutomatic ? [{
                                label: t("common.edit"),
                                icon: <EditIcon color="primary" />,
                                onClick: () => handleEdit(row.id),
                            }] : []),
                            {
                                label: t("common.lock"),
                                icon: <LockIcon color="primary" />,
                                onClick: () => handleLock([row.id], true),
                                disabled: !!row.isAutomatic,
                                tooltip: row.isAutomatic
                                    ? t(
                                        "dayBooking.automaticEntryLockingDisabled"
                                    )
                                    : null,
                            },
                            { divider: true },
                            {
                                label: t("common.remove"),
                                icon: <DeleteIcon color="error" />,
                                onClick: () => handleDelete(row),
                            },
                        ];

                    return (
                        <MuiMenu
                            label={
                                isLocked ? (
                                    <LockIcon
                                        sx={{
                                            color: isDisabled
                                                ? "gray"
                                                : theme.palette.primary.main,
                                        }}
                                    />
                                ) : (
                                    <MoreHorizIcon
                                        sx={{
                                            color: isDisabled
                                                ? "gray"
                                                : theme.palette.primary.main,
                                        }}
                                    />
                                )
                            }
                            items={items}
                            disabled={isDisabled}
                        />
                    );
                },
            },
        ],
        [
            dayBookingTypes,
            dayBookingTypeOptions,
            costCenterSelectOptions,
            vehicleSelectOptions,
            handleEdit,
            handleLock,
            handleDelete,
            editId,
            updateFullDayAmount,
            theme,
            t,
        ]
    );

    return (
        <>
            <LoadingIndicator loading={loading} />
            <MuiEditableDataGrid<DayBookingRow>
                persistStateKey="DayBookingDetailsListState"
                editId={editId}
                setEditId={setEditId}
                density="compact"
                className="day-booking-entries-table"
                sx={{ fontSize: "0.75rem" }}
                columns={cols}
                rows={gridRows}
                setRows={setGridRows}
                handleAddRow={handleAddRow}
                addRowText={t("dayBooking.newDayBooking")}
                fieldToFocus="dayBookingTypeId"
                handleSubmit={handleSubmit}
                validateRow={validateRow}
                isCellEditable={(params) => {
                    if (editingDisabled) return false;
                    const row = params.row as DayBookingRow;
                    if (row.isAutomatic) return false;
                    if (
                        EnumHelper.isEqual(
                            row.state,
                            DayBookingState.Locked
                        )
                    )
                        return false;
                    if (params.field === "amount") {
                        const type = dayBookingTypes?.find(
                            (type) => type.id === row.dayBookingTypeId
                        );
                        return !type || !isFullDayType(type);
                    }
                    return true;
                }}
                disableAddRowButton={editingDisabled}
                title={t("dayBooking.dayBookings")}
                hideFooter
                disableVirtualization={true}
                defaultSortModel={[{ field: "day", sort: "asc" }]}
                customToolbarButtons={[
                    ...(dayBookingItems?.length > 0 ? [{
                        key: "lock",
                        startIcon: allLocked ? <LockOpenIcon /> : <LockIcon />,
                        onClick: () =>
                            handleLock(
                                dayBookingItems.map((i) => i.id),
                                !allLocked
                            ),
                        children: allLocked
                            ? t("common.unlockAll")
                            : t("common.lockAll"),
                        variant: "text" as const,
                    }] : []),
                ]}
            />
        </>
    );
}
